/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ArrowLeft } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { QueryState } from '../../components/ui/query-state.js';
import { useIntake } from '../../lib/queries/intake.js';
import { IntakeReportDownload } from '../intake/intake-report-download.js';
import { IntakeReadOnlyView } from '../intake/patient-details-form.js';

/**
 * Therapist → read-only intake. The assigned therapist reviews everything the
 * family entered during intake and downloads the admin-generated PDF report
 * (BE scopes to assigned clients via ClientsService.getOne).
 */
export function TherapistClientIntakePage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const intake = useIntake(id);

  return (
    <div className="p-8">
      <Link
        to="/therapist/clients"
        className="focus-ring mb-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('therapist.clients.backToList')}
      </Link>
      <h1 className="mb-1 text-2xl font-bold text-slate-800 dark:text-white">
        {t('therapist.intake.title')}
      </h1>
      <p className="mb-6 text-sm text-slate-400">{t('therapist.intake.subtitle')}</p>

      <QueryState
        isLoading={intake.isLoading}
        isError={intake.isError}
        isEmpty={intake.data === undefined}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.intake.empty')}
      >
        {intake.data !== undefined && id !== undefined && (
          <div className="space-y-6">
            <IntakeReportDownload clientId={id} />
            <IntakeReadOnlyView view={intake.data} />
          </div>
        )}
      </QueryState>
    </div>
  );
}
